import AwsS3Multipart from '@uppy/aws-s3-multipart';
import { Uppy } from '@uppy/core';
import { DragDrop, useUppy } from '@uppy/react';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import React, { useState, useCallback, useRef } from 'react';
import { useMount } from 'react-use';
import text from '../../../text';
import { getUser } from '../../../utilities/storage';
import { Button } from '../../button';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import Icon from '../../icon/Icon';
import { useDispatch } from 'react-redux';
import { alertError } from '../../../redux/actions';
import {
  centerAspectCrop,
  createCroppedImage,
  DEFAULT_SLIDER_VALUE,
  CROPPER_HEIGHT,
  CROPPER_WIDTH,
  sliderValueToScale,
} from './utils';
import { v4 as uuidv4 } from 'uuid';
import { useUpdateCompanyLogoMutation } from '../../../redux/rtk';

const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const SelectImageForm = ({
  onSubmit,
  onCancel,
}: {
  onSubmit: () => void;
  onCancel: () => void;
}) => {
  const dispatch = useDispatch();
  const aspect = CROPPER_WIDTH / CROPPER_HEIGHT;
  const imgRef = useRef(null);

  const [crop, setCrop] = useState<Crop>();
  const [scale, setScale] = useState(1);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<PixelCrop | null>(
    null
  );
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const [fitScale, setFitScale] = useState(1);
  const [sliderValue, setSliderValue] = useState(DEFAULT_SLIDER_VALUE);

  const [updateCompanyLogo] = useUpdateCompanyLogoMutation();

  const onCropComplete = useCallback((pixelCrop: PixelCrop) => {
    setCroppedAreaPixels(pixelCrop);
  }, []);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (aspect) {
      const { width, height } = e.currentTarget;
      const newCrop = centerAspectCrop(width, height, aspect);
      setCrop(newCrop);

      // Calculate scale to fit image height to CROPPER_HEIGHT (80px)
      const newFitScale = CROPPER_HEIGHT / height;
      setFitScale(newFitScale);

      // initial scale to fit
      setScale(newFitScale);

      // initialize slider at middle
      setSliderValue(DEFAULT_SLIDER_VALUE);

      setImageReady(true);
    }
  };

  const uppy = useUppy(() => {
    const newUppy = new Uppy({
      debug: process.env.NODE_ENV === 'development',
      autoProceed: false,
      restrictions: {
        allowedFileTypes: ['.jpg', '.jpeg', '.png', '.webp', ...validTypes],
        maxNumberOfFiles: 1,
      },
    }).use(AwsS3Multipart, {
      limit: 1,
      companionUrl: `${process.env.UPPY_COMPANION_URL}/upload`,
      companionHeaders: {
        Authorization: `Bearer ${getUser().token}`,
      },
    });

    newUppy
      .on('restriction-failed', () => {
        setError(text('allowedImageFileTypes'));
      })
      .on('file-added', (file) => {
        if (!file.type || !validTypes.includes(file.type)) {
          uppy.removeFile(file.id);
          setError(text('allowedImageFileTypes'));
          return;
        }

        const { data } = file;
        if (data && data.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.addEventListener('load', () => {
            setImageReady(false);
            setImageSrc(reader.result as string);
            setSelectedFileId(file.id);
            setShowCropper(true);
          });

          try {
            reader.readAsDataURL(data);
          } catch (error) {
            console.error(`${text('errorReadingSelectedImage')}:`, error);
            dispatch(alertError(text('errorReadingSelectedImage')));
          }
        }

        uppy.setFileMeta(file.id, {
          filePath: `images/company-logo/${uuidv4()}`,
          uploadType: 'company-logo',
        });
      })
      .on('upload', () => {
        setUploading(true);
        setError(null);
      })
      .on('error', (error) => {
        setUploading(false);
        setError(error?.message);
      })
      .on('upload-error', (_, error) => {
        setUploading(false);
        setError(error?.message);
        newUppy.reset();
      });

    return newUppy;
  });

  useMount(() => () => {
    uppy.cancelAll();
  });

  const onHandleCancel = (event: React.SyntheticEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    uppy.cancelAll();
    setImageSrc(null);
    setShowCropper(false);
    onCancel();
  };

  const onHandleSubmit = async () => {
    try {
      if (imageSrc && croppedAreaPixels && selectedFileId && imgRef.current) {
        setUploading(true);
        const { blob } = await createCroppedImage(
          imgRef.current,
          croppedAreaPixels,
          scale
        );

        const fileName = `cropped-${new Date().getTime()}.jpg`;
        uppy.setFileState(selectedFileId, {
          data: blob,
          name: fileName,
          type: 'image/jpeg',
        });

        uppy
          .upload()
          .then(async (result) => {
            if (result.successful.length > 0) {
              const uploadedFile = result.successful[0];
              if (uploadedFile.meta.filePath) {
                await updateCompanyLogo({
                  filePath:
                    `${uploadedFile.meta.filePath}/${uploadedFile.name}` as string,
                }).unwrap();
                onSubmit();
              }
            }
          })
          .catch((error) => {
            console.error(`${text('errorUploadingImage')}:`, error);
            dispatch(alertError(text('errorUploadingImage')));
          });
      }
    } catch (error) {
      console.error(`${text('errorCroppingImage')}:`, error);
      dispatch(alertError(text('errorCroppingImage')));
    }
  };

  return (
    <Formik initialValues={{}} onSubmit={onHandleSubmit}>
      {() => (
        /* @ts-expect-error - Form component expects different props */
        <Form noValidate>
          {!showCropper ? (
            <div
              className={classNames('drag-drop-upload-container mt-4', {
                disabled: uploading,
                error,
              })}
            >
              <DragDrop
                uppy={uppy}
                note={text('dropFilesToUpload')}
                locale={{
                  strings: {
                    dropHereOr: text('dropHereOr'),
                    browse: text('browseToUpload'),
                  },
                }}
              />
              {error ? (
                <p className='Control-Feedback isFailed'>{error}</p>
              ) : null}
            </div>
          ) : (
            <>
              <p className='font-14 mt-4 mb-3 text-center font-weight--400'>
                {text('cropperHint', {
                  height: CROPPER_HEIGHT,
                  width: CROPPER_WIDTH,
                })}
              </p>
              <div className='crop-container'>
                {imageSrc ? (
                  <div
                    className='d-flex justify-content-center flex-row flex-fill crop-wrapper'
                    style={{ visibility: imageReady ? 'visible' : 'hidden' }}
                  >
                    <ReactCrop
                      locked={true}
                      crop={crop}
                      aspect={aspect}
                      onChange={(crop) => setCrop(crop)}
                      onComplete={(crop) => onCropComplete(crop)}
                      className='cropper'
                    >
                      <img
                        ref={imgRef}
                        src={imageSrc}
                        onLoad={onImageLoad}
                        style={{
                          transform: `scale(${scale})`,
                          transformOrigin: 'center',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          display: 'block',
                          margin: 'auto',
                        }}
                      />
                    </ReactCrop>
                  </div>
                ) : (
                  <div>{text('noImageSelected')}</div>
                )}
                {imageReady ? (
                  <div className='crop-controls d-flex flex-row align-items-center flex-fill position-relative'>
                    <label className='font-14 position-absolute mb-4'>
                      {text('resize')}
                    </label>
                    <Icon
                      icon='mdi_image-outline'
                      className='original size-16'
                    />
                    <div className='px-2 d-flex flex-fill'>
                      <input
                        type='range'
                        value={sliderValue}
                        min={0}
                        max={1}
                        step={0.01}
                        aria-labelledby='Zoom'
                        onChange={(e) => {
                          const newSliderValue = parseFloat(e.target.value);
                          setSliderValue(newSliderValue);
                          setScale(
                            sliderValueToScale(newSliderValue, fitScale)
                          );
                        }}
                        className='zoom-range w-100'
                      />
                    </div>
                    <Icon icon='mdi_image-outline' className='original' />
                  </div>
                ) : null}
              </div>
            </>
          )}
          <div className='form-footer px-4'>
            <Button
              disabled={!croppedAreaPixels}
              type='submit'
              loading={uploading}
            >
              {text('upload')}
            </Button>
            <a id='cancel' href='#' onClick={onHandleCancel}>
              {text('cancel')}
            </a>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default SelectImageForm;
